import React, {useEffect} from "react";
import Button from "@mui/joy/Button";
import Stack from "@mui/joy/Stack";
import IconButton from "@mui/joy/IconButton";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Divider from "@mui/joy/Divider";
import Sheet from "@mui/joy/Sheet";
import Input from "@mui/joy/Input";
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import {Game} from "../../models/games";
import ImageUploading, {ImageListType} from "react-images-uploading";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import Checkbox from "@mui/joy/Checkbox";
import Tooltip from "@mui/joy/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

type DefaultProps = {
    game: Game
    open: boolean
    setOpen: (e: boolean) => void
    onConfirm: (game: Game, file?: File) => void
}

const EditorDialog = (props: DefaultProps) => {
    const {game, open, setOpen, onConfirm} = props;
    const [name, setName] = React.useState(game.name);
    const [id, setId] = React.useState(game.id);
    const [alias, setAlias] = React.useState(game.alias);
    const [contextId, setContextId] = React.useState(game.contextId);
    const [inventoryLimit, setInventoryLimit] = React.useState(game.inventoryLimit);
    const [orderItemsLimit, setOrderItemsLimit] = React.useState(game.orderItemsLimit);
    const [sortOrder, setSortOrder] = React.useState(game.sortOrder);
    const [isActive, setIsActive] = React.useState(game.isActive);
    const [offlineBidsActivatePercent, setOfflineBidsActivatePercent] = React.useState(game.offlineBidsActivatePercent);
    const [offlineBidsDeactivatePercent, setOfflineBidsDeactivatePercent] = React.useState(game.offlineBidsDeactivatePercent);
    const [image, setImage] = React.useState<ImageListType>([]);
    const [imageFullSize, setImageFullSize] = React.useState('0');

    // Процент может быть не задан либо в диапазоне 0..100
    const isValidPercent = (value?: number) => value === undefined || (!Number.isNaN(value) && value >= 0 && value <= 100);
    const isActivatePercentValid = isValidPercent(offlineBidsActivatePercent);
    const isDeactivatePercentValid = isValidPercent(offlineBidsDeactivatePercent);

    useEffect(() => {
        if (image.length > 0) {
            setImageFullSize(new Intl.NumberFormat().format(image[0].file!.size / 1024 / 1024));
        } else if (game && game.icon) {
            fetch(game.icon, {method: 'HEAD'})
                .then(r => setImageFullSize(new Intl.NumberFormat().format(Number(r.headers.get('Content-Length')) / 1024 / 1024)));
        }
    }, [game, image]);

    return (
        <Modal open={open} onClose={() => setOpen(false)}>
            <ModalDialog sx={{maxHeight: '90vh'}}>
                <ModalClose/>
                <Typography id="filter-modal" level="h2">
                    {game.id > 0 ? 'Редактировать игру' : 'Новая игра'}
                </Typography>
                <Divider sx={{my: 2}}/>
                {/* Заголовок остается на месте, прокручивается только содержимое формы */}
                <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0, overflowY: 'auto', p: 0.5}}>
                    <ImageUploading
                        value={image}
                        onChange={(e) => setImage(e)}
                        maxNumber={1}
                    >
                        {({
                              onImageUpload,
                              onImageUpdate,
                          }) => (
                            <div className="upload__image-wrapper">
                                <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                    <img src={image.length === 1 ? image[0].dataURL : game?.icon}
                                         title={`${imageFullSize} Mb`}
                                         alt="" width="50" height="50"/>
                                    <IconButton color="primary"
                                                onClick={() => image.length === 1 ? onImageUpdate(0) : onImageUpload()}>
                                        <AddPhotoAlternateIcon/>
                                    </IconButton>
                                </Stack>
                            </div>
                        )}
                    </ImageUploading>
                    <FormControl disabled={!game.isNew}>
                        <FormLabel>App Id</FormLabel>
                        <Input placeholder="Id" value={id}
                               onChange={(event) => setId(Number(event.target.value))}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Название</FormLabel>
                        <Input placeholder="Название" value={name}
                               onChange={(event) => setName(event.target.value)}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Роут</FormLabel>
                        <Input placeholder="Роут" value={alias}
                               onChange={(event) => setAlias(event.target.value)}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Context Id</FormLabel>
                        <Input placeholder="ContextId" value={contextId}
                               onChange={(event) => setContextId(Number(event.target.value))}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Лимит инвентаря</FormLabel>
                        <Input placeholder="Лимит" value={inventoryLimit}
                               onChange={(event) => setInventoryLimit(Number(event.target.value))}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Лимит на заказ</FormLabel>
                        <Input placeholder="Лимит на заказ" value={orderItemsLimit ?? ''}
                               onChange={(event) => setOrderItemsLimit(event.target.value ? Number(event.target.value) : undefined)}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Порядок сортировки</FormLabel>
                        <Input placeholder="Порядок" type="number" value={sortOrder}
                               onChange={(event) => setSortOrder(Number(event.target.value))}/>
                    </FormControl>
                    <FormControl error={!isActivatePercentValid}>
                        <FormLabel>
                            Вкл.офлайн заявки, %
                            <Tooltip title="Офлайн заявки включаются, при суммарной заполненности инвентарей ботов менее указанного %">
                                <HelpOutlineIcon sx={{fontSize: 16, ml: 0.5, cursor: 'help'}}/>
                            </Tooltip>
                        </FormLabel>
                        <Input placeholder="%" type="number" value={offlineBidsActivatePercent ?? ''}
                               slotProps={{input: {min: 0, max: 100}}}
                               onChange={(event) => setOfflineBidsActivatePercent(event.target.value ? Number(event.target.value) : undefined)}/>
                        {!isActivatePercentValid && <FormHelperText>Значение от 0 до 100 или пусто</FormHelperText>}
                    </FormControl>
                    <FormControl error={!isDeactivatePercentValid}>
                        <FormLabel>
                            Выкл.офлайн заявки, %
                            <Tooltip title="Офлайн заявки выключаются, при суммарной заполненности инвентарей ботов более указанного %">
                                <HelpOutlineIcon sx={{fontSize: 16, ml: 0.5, cursor: 'help'}}/>
                            </Tooltip>
                        </FormLabel>
                        <Input placeholder="%" type="number" value={offlineBidsDeactivatePercent ?? ''}
                               slotProps={{input: {min: 0, max: 100}}}
                               onChange={(event) => setOfflineBidsDeactivatePercent(event.target.value ? Number(event.target.value) : undefined)}/>
                        {!isDeactivatePercentValid && <FormHelperText>Значение от 0 до 100 или пусто</FormHelperText>}
                    </FormControl>
                    <Checkbox label="Активна"
                              checked={isActive}
                              onChange={(event) => setIsActive(event.target.checked)}/>
                    <Button
                        onClick={() => setOpen(false)}
                        color="neutral"
                    >
                        Отмена
                    </Button>
                    <Button
                        disabled={!isActivatePercentValid || !isDeactivatePercentValid}
                        onClick={() => {
                            setOpen(false);
                            onConfirm({
                                id: id,
                                contextId: contextId,
                                name: name,
                                inventoryLimit: inventoryLimit,
                                orderItemsLimit: orderItemsLimit,
                                icon: game.icon,
                                alias: alias,
                                isNew:game.isNew,
                                isActive:isActive,
                                sortOrder:sortOrder,
                                offlineBidsEnabled: game.offlineBidsEnabled,
                                offlineBidsActivatePercent: offlineBidsActivatePercent,
                                offlineBidsDeactivatePercent: offlineBidsDeactivatePercent,
                            }, image.length === 1 ? image[0].file : undefined);
                        }}
                        color="primary"
                    >
                        Сохранить
                    </Button>
                </Sheet>
            </ModalDialog>
        </Modal>
    );
};

export default EditorDialog;